import { useState, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';

interface UseStreamingAIReturn {
  content: string;
  isStreaming: boolean;
  error: string | null;
  stream: (functionName: string, body: Record<string, unknown>) => Promise<void>;
  abort: () => void;
  reset: () => void;
}

export function useStreamingAI(): UseStreamingAIReturn {
  const [content, setContent] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    setContent('');
    setError(null);
    setIsStreaming(false);
  }, []);

  const abort = useCallback(() => {
    abortRef.current?.abort();
    setIsStreaming(false);
  }, []);

  const stream = useCallback(async (functionName: string, body: Record<string, unknown>) => {
    setIsStreaming(true);
    setContent('');
    setError(null);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${functionName}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({ error: { message: response.statusText } }));
        throw new Error(errData.error?.message ?? `HTTP ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const text = decoder.decode(value, { stream: true });
        const lines = text.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.content) {
                setContent((prev) => prev + data.content);
              }
              if (data.error) {
                setError(data.error);
              }
            } catch {
              // Skip malformed JSON
            }
          }
        }
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        setError((err as Error).message);
      }
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
    }
  }, []);

  return { content, isStreaming, error, stream, abort, reset };
}
